<script setup>
import {onMounted, ref} from "vue";
import Button from "../../components/forms/Button.vue";
import {getLoggedUser} from '@/services/AuthService';
import ClientService from "@/services/ClientService";
import IntermediaryPersonsService from "@/services/IntermediaryPersonsService";
import AccountManagersService from "@/services/AccountManagersService";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import {showSuccessMessage} from "@/helper";
import router from "@/router";
import {useDataTable} from "@/utils/datatableHelper";
import CreateUpdateClientModal from '@/components/modals/CreateUpdateClientModal.vue';

import {useI18n} from 'vue-i18n'

const {t} = useI18n();

const datatableRef = ref(null);
const datatable = useDataTable(fetchClients, 'fullname', 10, true, {}, 'CLIENTS-INDEX', datatableRef);
const intermediariesPeople = ref(null);
const accountManagers = ref(null);
const loggedUser = ref({});


const columns = ref([
  {
    label: t('clientIndex.name'),
    field: 'firstname',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: t('clientIndex.surname'),
    field: 'surname',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    }
  }, {
    label: t('clientIndex.email'),
    field: 'email',
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: t('clientIndex.intermediary_info'),
    field: 'intermediary_info',
    sortable: false,
    filterOptions: {
      enabled: true,
      skipDiacritics: true,
      placeholder: ' '
    }
  },
  {
    label: '',
    field: 'action',
    sortable: false,
    html: true
  },
]);

onMounted(() => {
  //fetchClients();
  Promise.all([getLoggedUser()])
      .then(([userResponse]) => {
        loggedUser.value = userResponse;
      });

  fetchIntermediariesPersons();
  fetchAccountManagers();
});

function fetchIntermediariesPersons() {
  IntermediaryPersonsService.getAll().then(response => {
    intermediariesPeople.value = response;
  });
}

function fetchAccountManagers() {
  AccountManagersService.getAll().then(response => {
    accountManagers.value = response;
  });
}

function fetchClients(body) {
  ClientService.fetchRecords(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}

function createClient() {
  const {reveal, onConfirm, onCancel, close} = createConfirmDialog(CreateUpdateClientModal, {intermediariesPeople: intermediariesPeople, accountManagers: accountManagers});
  reveal();
  onConfirm((response) => {
      router.push({name: 'ClientDetail', params: {id: response.client.id}});
  });
  onCancel(() => {
  });
}

function deleteUser(id) {
  ClientService.deleteClient(id).then(response => {
    showSuccessMessage(response.message);
    getClients();
  });
}

function navigateTo(to) {
  router.push(to);
}

</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('clientIndex.back') }}</a>
        </div>
        <div class="col-5 text-center">
          <div class="page-section__title">
            {{ t('clientIndex.clients') }}
          </div>
        </div>
        <div class="col-4 d-flex justify-content-end">
          <Button :label="t('clientIndex.add_client')" icon="mdi-text-box-plus" @click="createClient()"></Button>
        </div>
      </div>

    </div>
  </div>
  <div class="page-section__content">
    <div class="mt-3">
      <vue-good-table
          ref="datatableRef"
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @column-filter="datatable.onColumnFilter"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :pagination-options="{
            enabled: true,
            rowsPerPageLabel: t('clientIndex.entriesPerPage'),
            nextLabel: t('clientIndex.next'),
            prevLabel: t('clientIndex.previous'),
            ofLabel: 'z',
          }">
        <!-- Custom filter row — adds a × clear button to each text filter input -->
        <template #column-filter="{ column, updateFilters }">
          <div v-if="column.filterOptions?.enabled" class="filter-with-clear">
            <input
              type="text"
              class="vgt-input"
              :placeholder="column.filterOptions.placeholder ?? ''"
              :value="datatable.filter.value[column.field] ?? ''"
              @input="updateFilters(column, $event.target.value)"
            />
            <button
              v-if="datatable.filter.value[column.field]"
              type="button"
              class="filter-clear-btn"
              tabindex="-1"
              @click="updateFilters(column, '')"
            >
              &times;
            </button>
          </div>
        </template>

        <template #table-row="props">
                            <span v-if="props.column.field == 'action'">
                                <span style="font-weight: bold; float: right">
                                    <router-link :to="{ name: 'ClientDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
                                </span>
                            </span>
          <span v-if="props.column.field=='intermediary_info'">
                              <span>{{ props.row.intermediary_person?.label }}</span><br/>
                              <small>{{ props.row.intermediary_person?.intermediary?.label }}</small>
                            </span>
        </template>
        <template v-slot:emptystate v-if="!datatable.isLoading.value">
          <div class="text-center">
            {{ t('clientIndex.no_records_found') }}
          </div>
        </template>
        <template v-slot:emptystate v-if="datatable.isLoading.value">
          <div class="text-center">
            {{ t('clientIndex.loading') }}
          </div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<style scoped>
.filter-with-clear {
  position: relative;
}

/* Extra right padding so text doesn't overlap the × button */
.filter-with-clear .vgt-input {
  padding-right: 22px;
}

.filter-clear-btn {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 15px;
  padding: 0;
  line-height: 1;
}

.filter-clear-btn:hover {
  color: #555;
}
</style>
